import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react/dist/iconify.js";
import { motion } from "framer-motion";
import {
  getMisFortalezasThunk,
  clearMisFortalezas,
} from "../store/misFortalezas/misFortalezas.slice";
import { getPTQuizSummaryThunk } from "../store/preguntasTalentoQuiz/preguntasTalentoQuiz.thunks";
import "./test/Quiz/css/confirmacionSummary.css";
import Loader from "./UI/Loader";
import Alert from "./UI/Alert";
import EmptyState from "./UI/EmptyState";

// ---------------------------------------------------------------------------
// Etiquetas por tipo_fortaleza (1..7 según el mixin TipoFortalezaMixin).
// ---------------------------------------------------------------------------
const AREA_LABELS = {
  1: "¿Cómo construyo relaciones con los demás?",
  2: "¿Cómo aprendo y resuelvo problemas?",
  3: "¿Qué me motiva?",
  4: "¿Qué hago mejor que los demás?",
  5: "¿A qué me debo dedicar? Tus mejores tareas",
  6: "¿Cuál es mi rol en el trabajo de grupo?",
  7: "¿Quiénes pueden ayudarme a desarrollar mi potencial?",
};

const AREA_ICONS = {
  1: "mdi:account-group-outline",
  2: "mdi:puzzle-outline",
  3: "mdi:lightbulb-on-outline",
  4: "mdi:trophy-outline",
  5: "mdi:briefcase-check-outline",
  6: "mdi:account-supervisor-outline",
  7: "mdi:handshake-outline",
};

// Colores de acento por área — visibles en ambos modos
const AREA_COLORS = [
  { bg: "rgba(16,185,129,0.12)", fg: "#10b981" }, // verde brillante
  { bg: "rgba(99,102,241,0.12)", fg: "#818cf8" },  // índigo claro (legible en dark)
  { bg: "rgba(245,158,11,0.12)", fg: "#f59e0b" },  // ámbar brillante
];

// ---------------------------------------------------------------------------

const MisFortalezasLayer = () => {
  const dispatch = useDispatch();
  const { misFortalezas, loading, error } = useSelector(
    (state) => state.misFortalezas
  );
  const { summary: ptSummary, summaryLoading: ptSummaryLoading } = useSelector(
    (state) => state.preguntasTalentoQuiz
  );
  const ptPreguntas = ptSummary?.preguntas_talento || [];

  useEffect(() => {
    dispatch(getMisFortalezasThunk());
    dispatch(getPTQuizSummaryThunk());
    return () => {
      dispatch(clearMisFortalezas());
    };
  }, [dispatch]);

  // ---------------------------------------------------------------------------
  // Estados: carga / error
  // ---------------------------------------------------------------------------
  if (loading) {
    return (
      <div className="card radius-12">
        <div className="card-body p-24">
          <Loader center text="Cargando tus fortalezas..." delay={200} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card radius-12">
        <div className="card-body p-24">
          <Alert message={error} variant="error" size="md" />
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Estado: sin datos (ni del quiz viejo ni del nuevo)
  // ---------------------------------------------------------------------------
  if (!misFortalezas.length && !ptPreguntas.length && !ptSummaryLoading) {
    return (
      <div className="card radius-12">
        <div className="card-body p-24">
          <EmptyState
            icon="mdi:shield-star-outline"
            title="Aún no tienes fortalezas"
            description="Completa el cuestionario de fortalezas para descubrir tus talentos."
          />
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Vista principal
  // ---------------------------------------------------------------------------
  return (
    <div>
      {/* ── Banner de bienvenida ── */}
      <motion.div
        className="card radius-12 mb-20"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="card-body p-24">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle"
              style={{
                width: 52,
                height: 52,
                background: "linear-gradient(135deg, #10b981, #059669)",
                color: "#fff",
                flexShrink: 0,
              }}
            >
              <Icon icon="mdi:shield-star" width="28" />
            </div>
            <div>
              <h4 className="card-title mb-0">Mis Fortalezas</h4>
            </div>
          </div>
        </div>
      </motion.div>

      {/* ── Grid de áreas (resumen histórico del quiz viejo de fortalezas) ── */}
      {misFortalezas.length > 0 && (
      <div className="row">
        {misFortalezas.map((item, cardIndex) => {
          const label = AREA_LABELS[item.tipo_fortaleza] || item.pregunta;
          const icon =
            AREA_ICONS[item.tipo_fortaleza] || "mdi:help-circle-outline";
          const color = AREA_COLORS[cardIndex % AREA_COLORS.length];
          const totalFrases = item.talentos.reduce(
            (sum, t) => sum + (t.frases_seleccionadas?.length || 0),
            0
          );

          return (
            <motion.div
              key={cardIndex}
              className="col-lg-6 col-12 mb-20"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: 0.1 + cardIndex * 0.08,
                duration: 0.4,
                ease: "easeOut",
              }}
            >
              <div className="card radius-12 h-100 cs-talent-card">
                <div className="card-body p-20">
                  {/* ── Header del área ── */}
                  <div className="cs-talent-header">
                    <div
                      className="d-flex align-items-start gap-2"
                      style={{ flex: 1, minWidth: 0 }}
                    >
                      <div
                        className="cs-question-icon-wrap d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                        style={{
                          width: 36,
                          height: 36,
                          background: color.bg,
                          color: color.fg,
                        }}
                      >
                        <Icon icon={icon} width="18" />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        {item.tipo_fortaleza && (
                          <span
                            className="d-inline-block mb-1"
                            style={{
                              fontSize: "0.68rem",
                              fontWeight: 700,
                              letterSpacing: "0.14em",
                              textTransform: "uppercase",
                              color: color.fg,
                              background: color.bg,
                              padding: "2px 8px",
                              borderRadius: 999,
                            }}
                          >
                            Fortaleza #{item.tipo_fortaleza}
                          </span>
                        )}
                        <h5
                          className="cs-pregunta-title mb-0"
                        >
                          {label}
                        </h5>
                      </div>
                    </div>
                    <span
                      className="cs-talent-badge"
                      style={{ flexShrink: 0 }}
                    >
                      {totalFrases}{" "}
                      {totalFrases === 1 ? "respuesta" : "respuestas"}
                    </span>
                  </div>

                  {/* ── Talentos del área ── */}
                  {item.talentos.length === 0 ? (
                    <div className="cs-empty-state mt-12">
                      <Icon icon="mdi:minus-circle-outline" />
                      <span>Sin respuestas en esta área</span>
                    </div>
                  ) : (
                    <div className="d-flex flex-column gap-12 mt-12 cs-frases-scroll mis-fortalezas-scroll">
                      {item.talentos.map((t, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{
                            delay:
                              0.15 + cardIndex * 0.08 + i * 0.05,
                            duration: 0.26,
                          }}
                        >
                          {/* Chip del talento */}
                          <div className="d-flex align-items-center gap-2 mb-6">
                            <Icon
                              icon="mdi:star"
                              width="16"
                              style={{ color: color.fg, flexShrink: 0 }}
                            />
                            <span
                              className="text-primary-light fw-semibold"
                              style={{
                                fontSize: "0.85rem",
                              }}
                            >
                              {t.talento}
                            </span>
                          </div>

                          {/* Frases del talento en esta área */}
                          <div className="d-flex flex-column gap-2 ms-24">
                            {t.frases_seleccionadas.map((f, j) => (
                              <div
                                key={j}
                                className="d-flex align-items-start gap-2"
                              >
                                <Icon
                                  icon="mdi:check-circle"
                                  width="14"
                                  className="mt-1 flex-shrink-0"
                                  style={{ color: "#10b981" }}
                                />
                                <span
                                  className="text-secondary-light"
                                  style={{
                                    fontSize: "0.82rem",
                                    lineHeight: 1.5,
                                  }}
                                >
                                  {f.frase}
                                </span>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
      )}

      {/* ── Fortalezas 2.0 — mismo card que las "primeras fortalezas" ── */}
      {ptSummaryLoading && !ptPreguntas.length ? (
        <div className="card radius-12 mb-20">
          <div className="card-body p-24">
            <Loader
              center
              size="md"
              type="dots"
              delay={150}
              text="Cargando Fortalezas..."
            />
          </div>
        </div>
      ) : ptPreguntas.length > 0 ? (
        <div className="row">
          {ptPreguntas.map((item, cardIndex) => {
            const icon =
              AREA_ICONS[item.tipo_fortaleza] || "mdi:help-circle-outline";
            const color =
              AREA_COLORS[
                (cardIndex + misFortalezas.length) % AREA_COLORS.length
              ];
            const totalRespuestas = (item.talentos || []).reduce(
              (acc, t) => acc + (t.respuestas || []).length,
              0
            );

            return (
              <motion.div
                key={`pt-${item.frase_id}`}
                className="col-lg-6 col-12 mb-20"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: 0.1 + cardIndex * 0.08,
                  duration: 0.4,
                  ease: "easeOut",
                }}
              >
                <div className="card radius-12 h-100 cs-talent-card">
                  <div className="card-body p-20">
                    <div className="cs-talent-header">
                      <div
                        className="d-flex align-items-start gap-2"
                        style={{ flex: 1, minWidth: 0 }}
                      >
                        <div
                          className="cs-question-icon-wrap d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                          style={{
                            width: 36,
                            height: 36,
                            background: color.bg,
                            color: color.fg,
                          }}
                        >
                          <Icon icon={icon} width="18" />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          {item.tipo_fortaleza && (
                            <span
                              className="d-inline-block mb-1"
                              style={{
                                fontSize: "0.68rem",
                                fontWeight: 700,
                                letterSpacing: "0.14em",
                                textTransform: "uppercase",
                                color: color.fg,
                                background: color.bg,
                                padding: "2px 8px",
                                borderRadius: 999,
                              }}
                            >
                              Fortaleza #{item.tipo_fortaleza}
                            </span>
                          )}
                          <h5 className="cs-pregunta-title mb-0">
                            {item.frase}
                          </h5>
                        </div>
                      </div>
                      <span
                        className="cs-talent-badge"
                        style={{ flexShrink: 0 }}
                      >
                        {totalRespuestas}{" "}
                        {totalRespuestas === 1 ? "respuesta" : "respuestas"}
                      </span>
                    </div>

                    {(item.talentos || []).length === 0 ? (
                      <div className="cs-empty-state mt-12">
                        <Icon icon="mdi:minus-circle-outline" />
                        <span>Sin respuestas en esta fortaleza</span>
                      </div>
                    ) : (
                      <div className="d-flex flex-column gap-12 mt-12 cs-frases-scroll mis-fortalezas-scroll">
                        {item.talentos.map((t, i) => {
                          const respuestasT = t.respuestas || [];
                          if (respuestasT.length === 0) return null;

                          return (
                            <motion.div
                              key={t.talento_id || i}
                              initial={{ opacity: 0, x: -8 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{
                                delay: 0.15 + cardIndex * 0.08 + i * 0.05,
                                duration: 0.26,
                              }}
                            >
                              <div className="d-flex align-items-center gap-2 mb-6">
                                <Icon
                                  icon="mdi:star"
                                  width="16"
                                  style={{ color: color.fg, flexShrink: 0 }}
                                />
                                <span
                                  className="text-primary-light fw-semibold"
                                  style={{ fontSize: "0.85rem" }}
                                >
                                  {t.talento}
                                </span>
                              </div>

                              <div className="d-flex flex-column gap-2 ms-24">
                                {respuestasT.map((r) => (
                                  <div
                                    key={r.sub_frase_id}
                                    className="d-flex align-items-start gap-2"
                                  >
                                    <Icon
                                      icon={
                                        r.valor
                                          ? "mdi:check-circle"
                                          : "mdi:close-circle"
                                      }
                                      width="14"
                                      className="mt-1 flex-shrink-0"
                                      style={{
                                        color: r.valor ? "#10b981" : "#ef4444",
                                      }}
                                    />
                                    <span
                                      className="text-secondary-light"
                                      style={{
                                        fontSize: "0.82rem",
                                        lineHeight: 1.5,
                                      }}
                                    >
                                      {r.texto}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default MisFortalezasLayer;
